import React, { useCallback, useEffect, useRef, useState } from "react";
import GridCustomPage from "ui/grid/GridPage";
import { AnnualStatementFilter, getAnnualStatementApi } from "api/accounting";
import styles from "styles.module.css";
import { useTranslation } from "react-i18next";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { GridCellProps } from "@progress/kendo-react-grid";
import { PageTypePath } from "utils/pages";
import { ICON_NAMES } from "utils/constant";
import { AnnualStatementGridColumnItem } from "./GridColumns";
import Filter from "./Filter";
import { AnnualStatement as AnnualStatementItem } from "interface/accounting";
import AnnualStatementMobileList from "./AnnualStatementMobileList";
import "./styles.css";

const pageSize = 60;

const AnnualStatement = () => {
	const { t } = useTranslation();
	const gridRef = useRef<HTMLDivElement>(null);
	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
	const mobileNextSkip = useRef(0);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
	const [mobileData, setMobileData] = useState<AnnualStatementItem[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);
	const [filterOptions, setFilterOptions] = useState<AnnualStatementFilter>({
		take: pageSize,
		skip: 0,
		fromDate: null,
		toDate: null,
	});

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const requestMobileData = useCallback((skip: number, take: number, resetData = false) => {
		if (mobileRequestInProgress.current || mobileDataFinished.current) {
			return;
		}

		mobileRequestInProgress.current = true;
		setMobileLoading(true);

		getAnnualStatementApi({
			...filterOptions,
			skip,
			take,
		})
			.then((response) => {
				const incoming = (response?.data || []) as AnnualStatementItem[];

				if (incoming.length === 0) {
					mobileDataFinished.current = true;
					if (resetData) {
						setMobileData([]);
					}
					return;
				}

				mobileNextSkip.current = skip + take;

				if (resetData) {
					setMobileData(incoming);
				} else {
					setMobileData((prev) => [...prev, ...incoming]);
				}

				const loadedCount = skip + incoming.length;
				if (loadedCount >= (response?.totalCount || 0)) {
					mobileDataFinished.current = true;
				}
			})
			.finally(() => {
				mobileRequestInProgress.current = false;
				setMobileLoading(false);
			});
	}, [filterOptions]);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [isMobileView, requestMobileData]);

	const openPdfByRow = (prop: GridCellProps) => {
		localStorage.setItem("noteNumber", prop.dataItem.year);
		localStorage.setItem("pdfType", "YearlyRevenue");
		window.open(`/${PageTypePath.Pdf}/${prop.dataItem.reportGuid}`, "_blank");
		return;
	};

	const openPdfByItem = (item: AnnualStatementItem) => {
		if (!item.reportGuid) {
			return;
		}
		localStorage.setItem("noteNumber", String(item.year));
		localStorage.setItem("pdfType", "YearlyRevenue");

		window.open(`/${PageTypePath.Pdf}/${item.reportGuid}`, "_blank");
	};

	return (
		<div className={`${styles.PageContainer} annual-statement-page`}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>{t("AnnualReport")}</legend>
			</div>
			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				{isMobileView ? (
					<AnnualStatementMobileList
						items={mobileData}
						loading={mobileLoading}
						onOpenPdf={openPdfByItem}
						onLoadMore={() => requestMobileData(mobileNextSkip.current, pageSize)}
					/>
				) : (
					<GridCustomPage
						dataKey=""
						gridColumns={AnnualStatementGridColumnItem()}
						filter={filterOptions}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						lastColWidth="60px"
						apiMethod={getAnnualStatementApi}
						showDetailIcon={false}
						excelProps={{
							fileName: `annual_report_${new Date().toISOString()}.xlsx`,
						}}
						lastColBtns={[
							{
								onclick: openPdfByRow,
								iconName: ICON_NAMES.Download,
								alwaysShow: true,
							},
						]}
					/>
				)}
			</div>
		</div>
	);
};

export default AnnualStatement;
